import * as React from 'react'
import { useSelector } from 'react-redux'
import {
  Box,
  Typography as MUITypography,
  styled,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
} from '@mui/material'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { DataGrid, gridClasses, esES } from '@mui/x-data-grid'
import { getUserAll, getRole } from '../../../services'
import { grey } from '@mui/material/colors'
import { getColumns } from './columns'
import CreationModal from './CreationModal'
import DeleteUserModal from './DeleteUserModal'
import ResetPasswordModal from './ResetPasswordModal'
import { usePersistedState } from '../../../hooks/usePersistedState'
import AlertCustom from '../../Alerts'
import Botton from '../../BottonCustom'
import PeopleIcon from '@mui/icons-material/People'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import BlockIcon from '@mui/icons-material/Block'
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings'
import ScienceIcon from '@mui/icons-material/Science'
import PersonIcon from '@mui/icons-material/Person'
import PublicIcon from '@mui/icons-material/Public'
import SearchIcon from '@mui/icons-material/Search'
import ClearIcon from '@mui/icons-material/Clear'
import { Create } from '@mui/icons-material'

const STAT_CARDS = [
  { key: 'total', label: 'Total', color: '#4E6A5B', Icon: PeopleIcon },
  { key: 'habilitados', label: 'Habilitados', color: '#2e7d32', Icon: CheckCircleOutlineIcon },
  { key: 'noHabilitados', label: 'No habilitados', color: '#c62828', Icon: BlockIcon },
  { key: 'administradores', label: 'Administradores', color: '#1565c0', Icon: AdminPanelSettingsIcon },
  { key: 'investigadores', label: 'Investigadores', color: '#6a1b9a', Icon: ScienceIcon },
  { key: 'invitados', label: 'Invitados', color: '#e65100', Icon: PersonIcon },
  { key: 'publicos', label: 'Públicos', color: '#00838f', Icon: PublicIcon },
]

const styleHeaderTab = {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignContent: 'center',
  alignItems: 'baseline',
  ml: 4,
  mb: 1,
}

const Typography = styled(MUITypography)(props => ({
  fontFamily: props.theme.typography.fontFamily,
  fontStyle: 'normal',
  fontSize: '1em',
  fontWeight: 650,
  lineHeight: '18px',
  color: '#6A6A6A',
  paddingBottom: 6,
  paddingTop: 6,
  textAlign: 'center',
  marginTop: 12,
  marginBottom: 3,
  height: '5%',
}))

const styleDefaultButton = theme => ({
  color: theme.palette.primary.main,
  border: `1px solid ${theme.palette.primary.main}`,
  width: '100%',
  fontSize: '12px',
  '&:hover': {
    backgroundColor: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
  '&:active': { boxShadow: 'none', opacity: 0.9 },
})

const startOfDay = date => new Date(date.getFullYear(), date.getMonth(), date.getDate())
const endOfDay = date => new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999)

const UserTable = () => {
  const modalRef = React.useRef()
  const alertRef = React.useRef()
  const deleteModalRef = React.useRef()
  const resetModalRef = React.useRef()

  const handleRequestDelete = React.useCallback(user => {
    deleteModalRef.current?.openModal(user)
  }, [])

  const handleRequestReset = React.useCallback(user => {
    resetModalRef.current?.openModal(user)
  }, [])

  const [data, setData] = React.useState([])
  const [pageSize, setPageSize] = usePersistedState('admin.user.pageSize', 10)
  const [rowId, setRowId] = React.useState(null)
  const [allRoles, setAllRoles] = React.useState([])
  const [roleOptions, setRoleOptions] = React.useState([])
  const [activeFilter, setActiveFilter] = usePersistedState('admin.user.activeFilter', null)
  const [searchQuery, setSearchQuery] = usePersistedState('admin.user.searchQuery', '')
  const [dateFrom, setDateFrom] = React.useState(null)
  const [dateTo, setDateTo] = React.useState(null)

  const { isStored } = useSelector(state => state.alertsGlobal)
  const TOKEN = `Bearer ${window.localStorage.getItem('ArbolEc_token')}`

  const roleMap = React.useMemo(
    () => Object.fromEntries(allRoles.map(r => [r.id_r, r.tipo])),
    [allRoles],
  )

  const stats = React.useMemo(() => {
    const active = data.filter(u => u.habilitado)
    return {
      total: data.length,
      habilitados: active.length,
      noHabilitados: data.filter(u => !u.habilitado).length,
      administradores: active.filter(u => roleMap[u.idRol] === 'administrador').length,
      investigadores: active.filter(u => roleMap[u.idRol] === 'investigador').length,
      invitados: active.filter(u => roleMap[u.idRol] === 'invitado').length,
      publicos: data.filter(u => u.esPublico).length,
    }
  }, [data, roleMap])

  const filteredData = React.useMemo(() => {
    let result = data

    if (dateFrom || dateTo) {
      const from = dateFrom ? startOfDay(dateFrom) : null
      const to = dateTo ? endOfDay(dateTo) : null
      result = result.filter(u => {
        if (!u.fecha_registro_raw) return false
        if (from && u.fecha_registro_raw < from) return false
        if (to && u.fecha_registro_raw > to) return false
        return true
      })
    }

    switch (activeFilter) {
      case 'habilitados':
        result = result.filter(u => u.habilitado)
        break
      case 'noHabilitados':
        result = result.filter(u => !u.habilitado)
        break
      case 'administradores':
        result = result.filter(u => u.habilitado && roleMap[u.idRol] === 'administrador')
        break
      case 'investigadores':
        result = result.filter(u => u.habilitado && roleMap[u.idRol] === 'investigador')
        break
      case 'invitados':
        result = result.filter(u => u.habilitado && roleMap[u.idRol] === 'invitado')
        break
      case 'publicos':
        result = result.filter(u => u.esPublico)
        break
      default:
        break
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      result = result.filter(
        u =>
          u.nombre?.toLowerCase().includes(q) ||
          u.apellido?.toLowerCase().includes(q) ||
          u.email?.toLowerCase().includes(q),
      )
    }

    return result
  }, [data, activeFilter, dateFrom, dateTo, searchQuery, roleMap])

  const columns = React.useMemo(
    () => getColumns(rowId, setRowId, roleOptions, roleMap, handleRequestDelete, handleRequestReset),
    [rowId, roleOptions, roleMap, handleRequestDelete, handleRequestReset],
  )

  const handleCardClick = key => {
    setActiveFilter(prev => (key === 'total' || prev === key ? null : key))
  }

  const handleClearDates = () => {
    setDateFrom(null)
    setDateTo(null)
  }

  const handleFormCreation = () => {
    modalRef?.current?.openModal()
  }

  React.useEffect(() => {
    getRole(TOKEN)
      .then(res => {
        if (res?.roles) {
          setAllRoles(res.roles)
          setRoleOptions(res.roles.map(r => ({ value: r.id_r, label: r.tipo })))
        }
        return getUserAll(TOKEN)
      })
      .then(users => {
        if (users?.length) setData(users)
      })
      .catch(err => console.error('Error al cargar tabla de usuarios:', err.message))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  React.useEffect(() => {
    if (!Boolean(isStored)) return
    getUserAll(TOKEN)
      .then(users => {
        alertRef?.current?.openAlert(true)
        if (!users?.length) return
        setData(users)
      })
      .catch(err => console.error('Error al recargar usuarios:', err.message))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStored])

  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}>
      <Box sx={styleHeaderTab}>
        <Typography>Administrar Usuarios</Typography>
        <Box>
          <Botton
            text="Nuevo"
            onClick={handleFormCreation}
            startIcon={<Create />}
            styleButton={styleDefaultButton}
            size="small"
          />
        </Box>
      </Box>

      {/* Cards de estadísticas */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mx: 4, mb: 1.5 }}>
        {STAT_CARDS.map(({ key, label, color, Icon }) => {
          const isActive = key === 'total' ? activeFilter === null : activeFilter === key
          return (
            <Card
              key={key}
              variant="outlined"
              onClick={() => handleCardClick(key)}
              sx={{
                cursor: 'pointer',
                borderLeft: `3px solid ${color}`,
                boxShadow: isActive ? `inset 0 0 0 1.5px ${color}` : 'none',
                bgcolor: isActive ? `${color}18` : 'background.paper',
                transition: 'background-color 0.15s',
                '&:hover': { bgcolor: `${color}12` },
              }}>
              <CardContent
                sx={{
                  py: 0.5,
                  px: 1.25,
                  '&:last-child': { pb: 0.5 },
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                }}>
                <Icon sx={{ fontSize: 14, color }} />
                <MUITypography variant="caption" color="text.secondary" noWrap>
                  {label}
                </MUITypography>
                <MUITypography variant="caption" fontWeight={700} color={color}>
                  {stats[key]}
                </MUITypography>
              </CardContent>
            </Card>
          )
        })}
      </Box>

      {/* Buscador + filtro de fecha por rango */}
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Box sx={{ mx: 4, mb: 1.5, display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Buscar por nombre, apellido o email…"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            sx={{ width: 320 }}
          />
          <DatePicker
            label="Desde"
            value={dateFrom}
            onChange={setDateFrom}
            maxDate={dateTo ?? new Date()}
            inputFormat="dd/MM/yyyy"
            renderInput={params => <TextField {...params} size="small" sx={{ width: 150 }} />}
          />
          <DatePicker
            label="Hasta"
            value={dateTo}
            onChange={setDateTo}
            minDate={dateFrom ?? undefined}
            maxDate={new Date()}
            inputFormat="dd/MM/yyyy"
            renderInput={params => <TextField {...params} size="small" sx={{ width: 150 }} />}
          />
          {(dateFrom || dateTo) && (
            <Tooltip title="Limpiar fechas">
              <IconButton size="small" onClick={handleClearDates}>
                <ClearIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </LocalizationProvider>

      {/* DataGrid */}
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', px: 2, display: 'flex', flexDirection: 'column' }}>
        <DataGrid
          rows={filteredData}
          columns={columns}
          pageSize={pageSize}
          getRowId={row => row.id}
          rowsPerPageOptions={[10, 20, 50]}
          onPageSizeChange={newPageSize => setPageSize(newPageSize)}
          localeText={esES.components.MuiDataGrid.defaultProps.localeText}
          getRowSpacing={params => ({
            top: params.isFirstVisible ? 0 : 5,
            bottom: params.isLastVisible ? 0 : 5,
          })}
          sx={{
            flex: 1,
            [`& .${gridClasses.row}`]: { bgcolor: grey[100] },
          }}
          onCellEditCommit={params => setRowId(params.id)}
        />
      </Box>

      <CreationModal ref={modalRef} />
      <DeleteUserModal ref={deleteModalRef} />
      <ResetPasswordModal ref={resetModalRef} />
      <AlertCustom
        ref={alertRef}
        severity={isStored?.severity ?? (isStored?.stored !== true ? 'warning' : 'success')}>
        {isStored?.message ?? (isStored?.stored !== true ? 'No se pudo completar la operación.' : 'Guardado exitosamente.')}
      </AlertCustom>
    </Box>
  )
}

export default UserTable
